// ---// employeeMappingModal.tsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../core/data/redux/store';
import {
    createEsicEmployee,
    fetchEsicEmployees,
    updateEsicEmployee,
} from '../../../../core/data/redux/payroll/ESIC/employeeMapping/employeemappingAction';
import { fetchEsicBranchList } from '../../../../core/data/redux/payroll/ESIC/Master/Branch/branchAction';
import { getEmployees } from '../../../../core/data/redux/employee/employeeAction';
import { Drawer, message, Space, Select, Row, Col, Input, Form, DatePicker, Button } from 'antd';
import dayjs from 'dayjs';
import { ESICEmployee } from '../../../../core/data/redux/payroll/ESIC/employeeMapping/employeemappingType';

const { Option } = Select;

interface EmployeeMappingDrawerProps {
    open: boolean;
    onClose: () => void;
    editRecord: ESICEmployee | null;
    allEmployees: any[];
    allEmployeesLoading: boolean;
}

const EmployeeMappingDrawer: React.FC<EmployeeMappingDrawerProps> = ({
    open,
    onClose,
    editRecord,
    allEmployees,
    allEmployeesLoading,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();

    const { loading: esicEmployeeLoading } = useSelector((state: RootState) => state.esic.esicEmployee);
    const { branchList, loading: branchLoading } = useSelector((state: RootState) => state.esic.branch);

    useEffect(() => {
        if (open) {
            dispatch(getEmployees());
            dispatch(fetchEsicBranchList());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (editRecord) {
            form.setFieldsValue({
                employee_id: editRecord.employee_id || null,
                esic_number: editRecord.esic_number || '',
                esic_branch_id: editRecord.esic_branch_id || null,
                esic_enrollment_date: editRecord.esic_enrollment_date ? dayjs(editRecord.esic_enrollment_date) : null,
                is_esic_applicable: editRecord.is_esic_applicable ?? 1,
                status: editRecord.status || 'active',
                remarks: editRecord.remarks || '',
            });
        } else {
            form.resetFields();
        }
    }, [editRecord, form]);

    const handleSubmit = async (values: any) => {
        try {
            const payload = {
                ...values,
                esic_enrollment_date: values.esic_enrollment_date ? values.esic_enrollment_date.format('YYYY-MM-DD') : null,
                employee_id: values.employee_id,
                esic_branch_id: values.esic_branch_id || null,
                is_esic_applicable: values.is_esic_applicable,
            };

            if (editRecord) {
                await dispatch(updateEsicEmployee({ id: editRecord.esic_emp_id!, data: payload })).unwrap();
                message.success('Employee updated successfully');
            } else {
                await dispatch(createEsicEmployee(payload)).unwrap();
                message.success('Employee added successfully');
            }
            onClose();
            await dispatch(fetchEsicEmployees());
            form.resetFields();
        } catch (error: any) {
            message.error(error?.message || 'Operation failed');
        }
    };

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Drawer
            title={editRecord ? 'Edit ESIC Employee' : 'Add ESIC Employee'}
            placement="right"
            width={720}
            onClose={handleClose}
            open={open}
            bodyStyle={{ paddingBottom: 80, marginTop: 50 }}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button type="primary" onClick={() => form.submit()} disabled={esicEmployeeLoading === 'pending'}>
                            {esicEmployeeLoading === 'pending' && <span className="spinner-border spinner-border-sm me-2" />}
                            {editRecord ? 'Update' : 'Submit'}
                        </Button>
                    </Space>
                </div>
            }
        >
            <Form layout="vertical" form={form} onFinish={handleSubmit}>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="employee_id"
                            label="Employee"
                            rules={[{ required: true, message: 'Please select an employee!' }]}
                        >
                            <Select
                                placeholder="Select Employee"
                                loading={allEmployeesLoading}
                                showSearch
                                optionFilterProp="label"
                                disabled={!!editRecord}
                            >
                                {allEmployees?.length > 0 ? (
                                    allEmployees.map((emp: any) => (
                                        <Option key={emp.EmployeeID} value={emp.EmployeeID} label={emp.Name}>
                                            {emp.Name}
                                        </Option>
                                    ))
                                ) : (
                                    <Option disabled>{allEmployeesLoading ? 'Loading...' : 'No Employees Found'}</Option>
                                )}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="esic_number"
                            label="ESIC Number"
                            rules={[{ required: true, message: 'Please enter ESIC number!' }]}
                        >
                            <Input placeholder="Enter ESIC number" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="esic_branch_id"
                            label="ESIC Branch"
                            rules={[{ required: true, message: 'Please select an ESIC branch!' }]}
                        >
                            <Select
                                placeholder="Select ESIC Branch"
                                loading={branchLoading}
                                showSearch
                                optionFilterProp="label"
                            >
                                {branchList?.map((branch: any) => (
                                    <Option key={branch.branch_id} value={branch.branch_id} label={branch.branch_name}>
                                        {branch.branch_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="esic_enrollment_date"
                            label="Enrollment Date"
                            rules={[{ required: true, message: 'Please select enrollment date!' }]}
                        >
                            <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="is_esic_applicable"
                            label="Is ESIC Applicable?"
                            rules={[{ required: true, message: 'Please select an option!' }]}
                        >
                            <Select placeholder="Select an option">
                                <Option value={1}>Yes</Option>
                                <Option value={0}>No</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="remarks"
                            label="Remarks"
                        >
                            <Input.TextArea rows={2} placeholder="Enter remarks" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
};

export default EmployeeMappingDrawer;